import React, { useState, useEffect } from 'react';
import { X, Download, AlertCircle, Calendar, FileText, Users, CheckCircle, Loader } from 'lucide-react';
import { invoiceExportService, ExportProgress, ExportSummary } from '../../services/invoiceExportService';

interface ExportInvoicesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ExportInvoicesModal: React.FC<ExportInvoicesModalProps> = ({ isOpen, onClose }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [onlyWithPDF, setOnlyWithPDF] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [exportSummary, setExportSummary] = useState<ExportSummary | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

      setStartDate(firstDayOfMonth.toISOString().split('T')[0]);
      setEndDate(today.toISOString().split('T')[0]);
      setError('');
      setSummary(null);
      setExportSummary(null);
      setProgress(null);
      setOnlyWithPDF(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (startDate && endDate) {
      loadSummary();
    }
  }, [startDate, endDate]);

  const loadSummary = async () => {
    if (!startDate || !endDate) return;

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      setError('La fecha de inicio debe ser anterior a la fecha de fin');
      setSummary(null);
      return;
    }

    setLoadingSummary(true);
    setError('');

    try {
      const summaryData = await invoiceExportService.getInvoicesSummary(start, end);
      setSummary(summaryData);

      if (summaryData.totalInvoices === 0) {
        setError('No se encontraron facturas en el rango de fechas seleccionado');
      } else if (summaryData.withPDF === 0) {
        setError('Ninguna factura en este rango tiene archivos PDF adjuntos');
      }
    } catch (err) {
      console.error('Error cargando resumen:', err);
      setError('Error al cargar el resumen de facturas');
      setSummary(null);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleExport = async () => {
    if (!startDate || !endDate) {
      setError('Por favor selecciona ambas fechas');
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      setError('La fecha de inicio debe ser anterior a la fecha de fin');
      return;
    }

    if (!summary || summary.totalInvoices === 0) {
      setError('No hay facturas para exportar en el rango seleccionado');
      return;
    }

    if (onlyWithPDF && summary.withPDF === 0) {
      setError('No hay facturas con PDF en el rango seleccionado');
      return;
    }

    setLoading(true);
    setExporting(true);
    setError('');
    setProgress(null);
    setExportSummary(null);

    try {
      const result = await invoiceExportService.exportInvoicesToZip(
        start,
        end,
        (progressData) => {
          setProgress(progressData);
        },
        onlyWithPDF
      );

      if (!result) {
        throw new Error('No se pudo generar el archivo ZIP');
      }

      const { blob, summary: exportSum } = result;
      setExportSummary(exportSum);

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = invoiceExportService.generateZipFileName(start, end);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setTimeout(() => {
        setExporting(false);
        setProgress(null);
      }, 2000);

    } catch (err) {
      console.error('Error exportando facturas:', err);
      setError((err as Error).message || 'Error al exportar las facturas');
      setExporting(false);
      setProgress(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    invoiceExportService.cancelExport();
    setLoading(false);
    setExporting(false);
    setProgress(null);
    setError('Exportación cancelada');
  };

  if (!isOpen) return null;

  const progressPercentage = progress ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Download className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Exportar Facturas</h2>
              <p className="text-sm text-gray-500">Descarga facturas organizadas por colaborador</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4" />
                <span>Fecha Inicio</span>
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4" />
                <span>Fecha Fin</span>
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="onlyWithPDF"
              checked={onlyWithPDF}
              onChange={(e) => setOnlyWithPDF(e.target.checked)}
              disabled={loading}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
            />
            <label htmlFor="onlyWithPDF" className="text-sm text-gray-700 cursor-pointer">
              Exportar solo facturas con PDF adjunto
            </label>
          </div>

          {loadingSummary && (
            <div className="flex items-center justify-center py-4">
              <Loader className="w-5 h-5 animate-spin text-blue-600 mr-2" />
              <span className="text-sm text-gray-600">Cargando resumen...</span>
            </div>
          )}

          {summary && !loadingSummary && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center space-x-2">
                <FileText className="w-4 h-4" />
                <span>Resumen del Periodo</span>
              </h3>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Total Facturas</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.totalInvoices}</p>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Con PDF</p>
                  <p className="text-2xl font-bold text-green-600">{summary.withPDF}</p>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Sin PDF</p>
                  <p className="text-2xl font-bold text-orange-600">{summary.withoutPDF}</p>
                </div>
              </div>

              {summary.collaborators.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center space-x-1">
                    <Users className="w-3 h-3" />
                    <span>Por Colaborador</span>
                  </h4>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {summary.collaborators.map((collab: any, index: number) => (
                      <div key={index} className="flex items-center justify-between text-xs bg-white px-3 py-2 rounded">
                        <span className="text-gray-700 font-medium">{collab.name}</span>
                        <span className="text-gray-500">
                          {collab.count} facturas ({collab.withPDF} con PDF)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {exporting && progress && (
            <div className="bg-blue-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">
                  Descargando facturas...
                </span>
                <span className="text-sm font-semibold text-blue-600">
                  {progress.current} / {progress.total}
                </span>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-blue-600 transition-all duration-300 ease-out flex items-center justify-end pr-2"
                  style={{ width: `${progressPercentage}%` }}
                >
                  {progressPercentage > 10 && (
                    <span className="text-xs font-semibold text-white">{progressPercentage}%</span>
                  )}
                </div>
              </div>

              <div className="text-xs text-gray-600 space-y-1">
                <p>Colaborador: <span className="font-medium">{progress.collaboratorName}</span></p>
                <p>Archivo: <span className="font-medium">{progress.currentFileName}</span></p>
              </div>
            </div>
          )}

          {exportSummary && !exporting && (
            <div className="bg-green-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center space-x-2 text-green-800">
                <CheckCircle className="w-5 h-5" />
                <h3 className="text-sm font-semibold">Exportación Completada</h3>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-600">Facturas procesadas:</p>
                  <p className="font-semibold text-gray-900">{exportSummary.totalInvoices}</p>
                </div>
                <div>
                  <p className="text-gray-600">Descargas exitosas:</p>
                  <p className="font-semibold text-green-600">{exportSummary.successfulDownloads}</p>
                </div>
                <div>
                  <p className="text-gray-600">Plantilla:</p>
                  <p className="font-semibold text-gray-900">{exportSummary.collaboratorsCount}</p>
                </div>
                <div>
                  <p className="text-gray-600">Tamaño del archivo:</p>
                  <p className="font-semibold text-gray-900">
                    {invoiceExportService.formatFileSize(exportSummary.zipSizeBytes)}
                  </p>
                </div>
              </div>

              {exportSummary.failedDownloads > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded p-2 text-xs text-orange-800">
                  <p className="font-medium">Advertencia: {exportSummary.failedDownloads} facturas no se pudieron descargar</p>
                </div>
              )}

              {exportSummary.missingPDFs > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800">
                  <p className="font-medium">Nota: {exportSummary.missingPDFs} facturas no tienen PDF adjunto</p>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {summary && summary.withoutPDF > 0 && !onlyWithPDF && !error && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800">Advertencia</p>
                <p className="text-sm text-yellow-700 mt-1">
                  Hay {summary.withoutPDF} facturas sin PDF adjunto que no se incluirán en la exportación.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end space-x-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
          {exporting ? (
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-red-300 rounded-lg text-red-700 hover:bg-red-50 transition-colors"
            >
              Cancelar
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cerrar
              </button>
              <button
                onClick={handleExport}
                disabled={loading || !summary || summary.totalInvoices === 0 || (onlyWithPDF && summary.withPDF === 0)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                <span>{loading ? 'Exportando...' : 'Exportar'}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportInvoicesModal;
